import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { appListenerMiddleware } from 'reduxTyped'
import { AiWelcomeMessageAnalytics } from 'apps/aiCommunications/components/AiWelcomeMessage/lib/analytics'
import { AiCommunicationsState, WelcomeMessageSource } from 'apps/aiCommunications/store/types'
import { billing, UpgradeSource } from 'common/billing'
import { upgrade } from 'common/billing/actions/upgradeActions'
import { Ability } from 'common/core/constants/Ability'
import { getAbility, getHasAbility } from 'common/core/selectors/abilitiesSelectors'
import { makeCreateAsyncAction } from 'shared/lib/redux'

const createAsyncAction = makeCreateAsyncAction({
  scope: 'ai',
  section: 'ai_communications',
})

export const AiCommunicationsInitialState: AiCommunicationsState = {
  triggeredBySetLive: false,
  triggeredByPurchase: false,
  isWelcomeMessageVisible: false,
  showAfterUpgrade: false,
  welcomeMessageSource: WelcomeMessageSource.BILLING,
}

const NAMESPACE = 'aiCommunications'

const { reducer, actions } = createSlice({
  initialState: AiCommunicationsInitialState,
  name: NAMESPACE,
  reducers: {
    hideWelcomeMessage: (state) => {
      state.isWelcomeMessageVisible = false
    },
    showWelcomeMessage: (state, action: PayloadAction<WelcomeMessageSource>) => {
      state.isWelcomeMessageVisible = true
      state.welcomeMessageSource = action.payload
    },
    setTriggeredBySetLive: (state, action: PayloadAction<boolean>) => {
      state.triggeredBySetLive = action.payload
    },
    setTriggeredByPurchase: (state, action: PayloadAction<boolean>) => {
      state.triggeredByPurchase = action.payload
    },
    showAfterUpgradeUpdated: (state, action: PayloadAction<boolean>) => {
      state.showAfterUpgrade = action.payload
    },
  },
})

const tryOpenWelcomeMessageModal = createAsyncAction(
  `${NAMESPACE}/tryOpenWelcomeMessageModal`,
  (force: boolean, { dispatch, getState }) => {
    const state = getState()
    if (force || AiCommunicationsSelectors.getTriggeredByPurchase(state)) {
      dispatch(AiCommunicationsActions.showWelcomeMessage(WelcomeMessageSource.BILLING))
    }
  },
)

export const AiCommunicationsActions = {
  tryOpenWelcomeMessageModal,
  hideWelcomeMessage: actions.hideWelcomeMessage,
  showWelcomeMessage: actions.showWelcomeMessage,
  setTriggeredBySetLive: actions.setTriggeredBySetLive,
  setTriggeredByPurchase: actions.setTriggeredByPurchase,
}

const getState = (state: RootState) => state.aiCommunications

export const AiCommunicationsSelectors = {
  getState,
  getWelcomeMessageVisible: (state: RootState) => getState(state).isWelcomeMessageVisible,
  getWelcomeMessageSource: (state: RootState) => getState(state).welcomeMessageSource,
  getTriggeredBySetLive: (state: RootState) => getState(state).triggeredBySetLive,
  getTriggeredByPurchase: (state: RootState) => getState(state).triggeredByPurchase,
}

export const AiCommunicationsReducer = reducer

appListenerMiddleware.startListening({
  actionCreator: upgrade.pending,
  effect: (_, { getState, dispatch }) => {
    const agentAbility = getAbility(getState(), Ability.AI_AGENT)

    dispatch(actions.showAfterUpgradeUpdated(!agentAbility.isEnabled))
  },
})

appListenerMiddleware.startListening({
  actionCreator: upgrade.fulfilled,
  effect: ({ meta }, { getState, dispatch }) => {
    const state = getState()

    if (AiCommunicationsSelectors.getTriggeredBySetLive(state)) {
      dispatch(AiCommunicationsActions.setTriggeredByPurchase(true))
      return
    }

    const { showAfterUpgrade } = AiCommunicationsSelectors.getState(getState())

    if (
      getHasAbility(state, Ability.AI_AGENT) &&
      meta.arg.source !== UpgradeSource.AI_ALWAYS_ON &&
      showAfterUpgrade &&
      !billing.isV3()
    ) {
      dispatch(AiCommunicationsActions.tryOpenWelcomeMessageModal(true))
    }
  },
})

appListenerMiddleware.startListening({
  actionCreator: actions.showWelcomeMessage,
  effect: (action) => {
    AiWelcomeMessageAnalytics.logModalOpened({ source: action.payload })
  },
})
